import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';

const FeaturedProducts = () => {
  const [isPaused, setIsPaused] = useState(false);
  const [products, setProducts] = useState([]);
  const carouselRef = useRef(null);

  useEffect(() => {
    // Load products from localStorage
    const savedProducts = JSON.parse(localStorage.getItem('kassim-products')) || [];
    setProducts(savedProducts);
  }, []);

  const handleTouchStart = () => setIsPaused(true);
  const handleTouchEnd = () => setIsPaused(false);

  // If no products in admin, use default products
  const featuredProducts = products.length > 0 ? products : [
    { 
      name: 'Angle Valve Chrome', 
      price: 'KSH 2,499', 
      originalPrice: 'KSH 3,200',
      category: 'Plumbing', 
      code: '510052',
      rating: 4.5,
      reviews: 128,
      image: ''
    },
    { 
      name: 'Circuit Breaker 20A', 
      price: 'KSH 4,599', 
      originalPrice: 'KSH 5,800',
      category: 'Electrical', 
      code: 'EL-201',
      rating: 4.8,
      reviews: 89,
      image: ''
    },
    { 
      name: 'Professional Pipe Wrench', 
      price: 'KSH 3,575', 
      originalPrice: 'KSH 4,500',
      category: 'Tools', 
      code: 'TL-301',
      rating: 4.3,
      reviews: 204,
      image: ''
    },
    { 
      name: 'LED Panel Light 2x4', 
      price: 'KSH 8,950', 
      originalPrice: 'KSH 11,200',
      category: 'Electrical', 
      code: 'EL-202',
      rating: 4.7,
      reviews: 156,
      image: ''
    },
    { 
      name: 'PVC Pipe 2" x 10ft', 
      price: 'KSH 3,299', 
      originalPrice: 'KSH 4,000',
      category: 'Plumbing', 
      code: 'PL-102',
      rating: 4.4,
      reviews: 312,
      image: ''
    },
    { 
      name: 'Voltage Tester Digital', 
      price: 'KSH 2,450', 
      originalPrice: 'KSH 3,100',
      category: 'Electrical', 
      code: 'EL-203',
      rating: 4.6,
      reviews: 178,
      image: ''
    },
  ];

  // Duplicate for seamless loop
  const duplicatedProducts = [...featuredProducts, ...featuredProducts];

  const getCategoryIcon = (category) => {
    switch(category) {
      case 'Plumbing': return '🚰';
      case 'Electrical': return '⚡';
      case 'Tools': return '🛠️';
      case 'Pipes': return '📏';
      case 'Fittings': return '🔩';
      case 'Lighting': return '💡';
      default: return '📦';
    }
  };

  const getCategoryColor = (category) => {
    switch(category) {
      case 'Plumbing': return 'bg-blue-500';
      case 'Electrical': return 'bg-yellow-500';
      case 'Tools': return 'bg-gray-500';
      case 'Pipes': return 'bg-green-500';
      case 'Fittings': return 'bg-purple-500';
      case 'Lighting': return 'bg-orange-500';
      default: return 'bg-gray-500';
    }
  };

  const getCategoryBg = (category) => {
    switch(category) {
      case 'Plumbing': return 'bg-blue-50';
      case 'Electrical': return 'bg-yellow-50';
      case 'Tools': return 'bg-gray-50';
      case 'Pipes': return 'bg-green-50';
      case 'Fittings': return 'bg-purple-50';
      case 'Lighting': return 'bg-orange-50';
      default: return 'bg-gray-50';
    }
  };

  return (
    <section className="py-12 bg-gray-50" id="featured">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl font-bold text-gray-800">Featured Products</h2>
          <a 
            href="/admin" 
            className="text-orange-500 font-semibold hover:text-orange-600 transition-colors"
          >
            🔧 Manage Products →
          </a>
        </div>

        {/* Carousel Container */}
        <div 
          className="relative overflow-hidden"
          ref={carouselRef}
          onMouseEnter={handleTouchStart}
          onMouseLeave={handleTouchEnd}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <motion.div
            className="flex space-x-6"
            animate={{ x: [0, -50 + '%'] }}
            transition={{
              x: {
                repeat: Infinity,
                repeatType: "loop",
                duration: 40,
                ease: "linear",
              },
            }}
            style={{ 
              width: 'max-content',
              animationPlayState: isPaused ? 'paused' : 'running'
            }}
          >
            {duplicatedProducts.map((product, index) => (
              <motion.div
                key={`${product.id || product.code}-${index}`}
                className="flex-shrink-0 w-80 bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-300"
                whileHover={{ y: -3 }}
              >
                {/* Product Image Area */}
                <div className={`h-48 flex items-center justify-center ${getCategoryBg(product.category)}`}>
                  {product.image ? (
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className="text-center" style={{ display: product.image ? 'none' : 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div className={`w-16 h-16 rounded-lg mx-auto mb-3 flex items-center justify-center ${getCategoryColor(product.category)}`}>
                      <span className="text-white text-xl">
                        {getCategoryIcon(product.category)}
                      </span>
                    </div>
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                      product.category === 'Plumbing' ? 'bg-blue-100 text-blue-700' : 
                      product.category === 'Electrical' ? 'bg-yellow-100 text-yellow-700' :
                      product.category === 'Tools' ? 'bg-gray-100 text-gray-700' :
                      product.category === 'Pipes' ? 'bg-green-100 text-green-700' :
                      product.category === 'Fittings' ? 'bg-purple-100 text-purple-700' :
                      'bg-orange-100 text-orange-700'
                    }`}>
                      {product.category}
                    </span>
                  </div>
                </div>

                {/* Product Info */}
                <div className="p-4">
                  <h3 className="font-semibold text-gray-800 mb-2 text-sm leading-tight">
                    {product.name}
                  </h3>
                  
                  {/* Rating */}
                  <div className="flex items-center space-x-1 mb-2">
                    <div className="flex text-yellow-400 text-sm">
                      {'★'.repeat(Math.floor(product.rating || 4.5))}
                      {'☆'.repeat(5 - Math.floor(product.rating || 4.5))}
                    </div>
                    <span className="text-xs text-gray-500">({product.reviews || 100})</span>
                  </div>

                  {/* Pricing */}
                  <div className="flex items-center space-x-2 mb-3">
                    <span className="text-lg font-bold text-gray-800">{product.price}</span>
                    {product.originalPrice && (
                      <span className="text-sm text-gray-500 line-through">{product.originalPrice}</span>
                    )}
                  </div>

                  {/* Add to Cart */}
                  <motion.button
                    className="w-full bg-orange-500 text-white py-2 rounded-lg text-sm font-semibold hover:bg-orange-600 transition-colors"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Add to Cart
                  </motion.button>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Gradient Overlays */}
          <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-gray-50 to-transparent pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-gray-50 to-transparent pointer-events-none" />
        </div>

        {/* Admin Hint */}
        {products.length === 0 && (
          <div className="text-center mt-6">
            <p className="text-sm text-gray-500">
              💡 <a href="/admin" className="text-orange-500 hover:underline">Add your products in the admin panel</a> to see them here!
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturedProducts;
